import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { logger } from '@/utils/logger';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  is_active: boolean;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: string;
  organization?: Organization;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  userRole: string | null;
  organizations: Organization[];
  loadingOrg: boolean;
  switchOrganization: (orgId: string) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider = ({ children }: OrganizationProviderProps) => {
  const { user, session } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrg, setLoadingOrg] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchOrganizations = async () => {
      if (!user) {
        if (mounted) {
          setCurrentOrganization(null);
          setUserRole(null);
          setOrganizations([]);
          setLoadingOrg(false);
        }
        return;
      }

      try {
        setLoadingOrg(true);
        // Fetch the user's direct memberships
        const { data: membersData, error: membersError } = await (supabase.from as any)('organization_members')
          .select('id, role, organization_id')
          .eq('user_id', user.id);

        // If org tables don't exist, use fallback for single-tenant mode
        if (membersError) {
          const isTableMissing = membersError.message?.includes('relation') || 
                                  membersError.code === '42P01' ||
                                  membersError.message?.includes('does not exist');
          if (isTableMissing) {
            logger.warn('Tabelas de organização não encontradas. Usando fallback single-tenant.');
            const fallbackOrg: Organization = {
              id: 'default',
              name: 'Default',
              slug: 'default',
              plan: 'free',
              is_active: true
            };
            if (mounted) {
              setOrganizations([fallbackOrg]);
              setCurrentOrganization(fallbackOrg);
              setUserRole('ADMIN');
              setLoadingOrg(false);
            }
            return;
          }
          throw membersError;
        }

        if (membersData && membersData.length > 0) {
          // Then fetch the actual organizations they are a part of
          const orgIds = membersData.map(m => m.organization_id);
          const { data: orgsData, error: orgsError } = await (supabase.from as any)('organizations')
            .select('*')
            .in('id', orgIds)
            .eq('is_active', true);
            
          if (orgsError) throw orgsError;

          if (mounted && orgsData && orgsData.length > 0) {
            setOrganizations(orgsData as Organization[]);
            
            // Check if there's a previously selected org in localStorage
            const savedOrgId = localStorage.getItem('tenant_organization_id');
            const savedOrg = savedOrgId ? (orgsData as Organization[]).find((o: Organization) => o.id === savedOrgId) : null;
            
            const selectedOrg = savedOrg || (orgsData as Organization[])[0];
            setCurrentOrganization(selectedOrg);
            
            // Find role for this specific org
            const memberRecord = (membersData as any[]).find((m: any) => m.organization_id === selectedOrg.id);
            setUserRole(memberRecord?.role || 'PADRAO');
            
            // Store it just in case it wasn't there
            localStorage.setItem('tenant_organization_id', selectedOrg.id);
          }
        } else {
          if (mounted) {
            setOrganizations([]);
            setCurrentOrganization(null);
            setUserRole(null);
            logger.warn('Usuário não pertence a nenhuma organização');
          }
        }
      } catch (error) {
        logger.error('Erro ao buscar organizações do usuário', error);
      } finally {
        if (mounted) setLoadingOrg(false);
      }
    };

    fetchOrganizations();

    return () => {
      mounted = false;
    };
  }, [user, session]);

  const switchOrganization = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setCurrentOrganization(org);
      localStorage.setItem('tenant_organization_id', org.id);
      
      // Update role based on new org
      (supabase.from as any)('organization_members')
        .select('role')
        .eq('user_id', user?.id)
        .eq('organization_id', orgId)
        .single()
        .then(({ data }: any) => {
          if (data) {
            setUserRole(data.role);
          }
          // Forçar um recarregamento da página para limpar states antigs se necessário,
          // Ou depender da reatividade do React Query invalidateQueries (Recomendado via hook/evento).
          window.location.reload(); 
        });
    }
  };

  const value = {
    currentOrganization,
    userRole,
    organizations,
    loadingOrg,
    switchOrganization
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
